import { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Piece colors picked to read well on the CTA gradient background
const COLORS = ['#FA3F58', '#4A90D9', '#69EDE9', '#FFC93C', '#A479F9', '#FFFFFF', '#FA7559'];

// How long the burst lasts before the overlay unmounts (covers the longest
// piece: max duration 2.6s + max delay 0.2s, with a little slack)
const TOTAL_MS = 3000;

/*
  The parabola is split across two nested elements: the launcher moves
  horizontally at constant speed while the piece inside rises with ease-out
  and falls with ease-in — combined they trace a ballistic arc.
*/
const flyX = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(var(--tx)); }
`;

const flyY = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 1;
    animation-timing-function: cubic-bezier(0.17, 0.67, 0.4, 0.96);
  }
  55% {
    transform: translateY(var(--peak)) rotate(calc(var(--rot) * 0.55));
    opacity: 1;
    animation-timing-function: cubic-bezier(0.6, 0.04, 0.98, 0.34);
  }
  100% {
    transform: translateY(var(--fall)) rotate(var(--rot));
    opacity: 0;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const Launcher = styled.div`
  position: absolute;
  left: 50%;
  bottom: 30%;
  animation: ${flyX} var(--duration) linear var(--delay) both;
`;

const Piece = styled.div`
  border-radius: 2px;
  animation: ${flyY} var(--duration) linear var(--delay) both;
`;

/*
  One-shot confetti burst for the CTA success screen: pieces spring from the
  center outward to the left and right once, arc down while fading, then the
  whole overlay unmounts. Trajectories are randomized once on mount (useMemo)
  so re-renders don't reshuffle them mid-flight.
*/
const Confetti = ({ count = 60 }) => {
  const [done, setDone] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDone(true), TOTAL_MS);
    return () => clearTimeout(timer);
  }, []);

  const pieces = useMemo(
    () =>
      Array.from({ length: count }, (_, i) => {
        const goesLeft = i % 2 === 0;
        const distance = 0 + Math.random() * 155; // vw travelled away from the center
        const scale = 0.6 + Math.random();
        const spin = (180 + Math.random() * 540) * (Math.random() < 0.5 ? -1 : 1);

        return {
          launcherStyle: {
            // custom properties inherit — the inner Piece reads duration/delay too
            '--tx': `${goesLeft ? -distance : distance}vw`,
            '--duration': `${(1.6 + Math.random()).toFixed(2)}s`,
            '--delay': `${(Math.random() * 0.2).toFixed(2)}s`
          },
          pieceStyle: {
            width: `${Math.round(7 * scale)}px`,
            height: `${Math.round(12 * scale)}px`,
            backgroundColor: COLORS[i % COLORS.length],
            '--peak': `-${Math.round(35 + Math.random() * 40)}vh`,
            '--fall': `${Math.round(25 + Math.random() * 30)}vh`,
            '--rot': `${Math.round(spin)}deg`
          }
        };
      }),
    [count]
  );

  if (done) return null;

  return (
    <Overlay aria-hidden>
      {pieces.map(({ launcherStyle, pieceStyle }, i) => (
        <Launcher key={i} style={launcherStyle}>
          <Piece style={pieceStyle} />
        </Launcher>
      ))}
    </Overlay>
  );
};

export default Confetti;
